import React, { FC } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { logEventAsArray } from '@/containers/AnalyticsContainer';
import { ILayoutAnalytics } from '@/layouts/layout.types';
import { StickyHeader } from '@/layouts/StickyHeader';
import { Icon } from '@/components/atomic/Icon';
import BottomSheet from '@/components/BottomSheet';
import { openHintBottomSheet } from '@/common/components/Hint';
import LIcon from '@/layouts/Base/components/LIcon';
import SupportIcon from '@/layouts/Base/components/SupportIcon';
import TopMenuIcon from '@/layouts/Base/components/TopMenuIcon';
import { useGetBackUrl } from '@/hooks/backUrl';
import { useBreakPointDown } from '@/hooks/breakPoint';
import { classnames } from '@/utils/classnames';

export interface ICardBackHeaderProps {
  title: string | string[];
  onBackClick?: () => void;
  hasCardHeaderBorder?: boolean;
  hiddenStoryContainer?: boolean;
  classNames?: Partial<Record<'root' | 'title' | 'icon', string>>;
  showSupportButtonOnMobile?: boolean;
  showSupportButtonOnDesktop?: boolean;
  isHeaderSticky?: boolean;
  hintComponent?: React.ReactNode;
  analytics?: ILayoutAnalytics;
}

const CardBackHeader: FC<ICardBackHeaderProps> = ({
  title,
  hasCardHeaderBorder = true,
  classNames,
  showSupportButtonOnMobile = true,
  showSupportButtonOnDesktop = false,
  isHeaderSticky = false,
  hiddenStoryContainer = false,
  hintComponent,
  analytics,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const backUrl = useGetBackUrl();
  const isMdDown = useBreakPointDown('md');

  const handleBackUrl = () => {
    if (backUrl) router.replace(backUrl);
    else router.back();
  };

  const handleHintClick = () => {
    BottomSheet.open({
      children: hintComponent,
    });
  };

  const toggleStoryContainerShow = () => {
    if (analytics?.supportButton) {
      logEventAsArray(analytics.supportButton);
    }
    openHintBottomSheet({ shouldOpenFullPage: false });
  };

  const returnTitle = () => {
    const step = searchParams?.get('step');
    if (!title) return '';
    if (typeof title === 'string') return title;
    return title?.[((step as unknown as number) || 1) - 1];
  };

  const header = (
    <>
      <div
        className={classnames(
          'mb-4 flex items-center justify-between border-solid border-gray-20 !px-5 md:mb-0 md:pb-4',
          hasCardHeaderBorder && 'md:!mb-5 md:border-b',
          classNames?.root
        )}
      >
        <LIcon
          className={classnames(
            'bg-gray-00',
            !showSupportButtonOnDesktop &&
              'md:h-[30px] md:w-[30px] md:!text-[16px]',
            classNames?.icon
          )}
          icon="chevron_right"
          onClick={handleBackUrl}
        />
        <div className="flex flex-grow justify-center md:mr-2 md:justify-start">
          <div
            className={classnames(
              'flex items-center justify-between gap-x-1 !text-sm font-demibold leading-3 text-gray-110 md:flex-grow md:!text-xs',
              classNames?.title,
              Boolean(hintComponent) && 'cursor-pointer md:cursor-default'
            )}
            onClick={hintComponent && isMdDown ? handleHintClick : undefined}
          >
            {returnTitle()}
            {hintComponent && (
              <div
                className="inline-flex items-center justify-center rounded-lg md:h-7 md:w-7 md:cursor-pointer md:bg-gray-00"
                onClick={
                  hintComponent && !isMdDown ? handleHintClick : undefined
                }
              >
                <Icon color="green-20" icon="info_outline" size={20} />
              </div>
            )}
          </div>
        </div>

        {hiddenStoryContainer ? (
          <SupportIcon
            iconClassName={classnames(
              'bg-gray-00',
              !showSupportButtonOnDesktop && 'md:!hidden',
              !showSupportButtonOnMobile && 'invisible',
              classNames?.icon
            )}
          />
        ) : (
          <TopMenuIcon
            className={classnames(
              '!bg-gray-00',
              !showSupportButtonOnDesktop && 'md:!hidden',
              !showSupportButtonOnMobile && 'invisible',
              classNames?.icon
            )}
            onClick={toggleStoryContainerShow}
          />
        )}
      </div>
    </>
  );

  return isHeaderSticky ? <StickyHeader>{header}</StickyHeader> : header;
};

export default CardBackHeader;
